import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

export default class extends Controller {
  static targets = ['field', 'count', 'message'];

  static values = {
    autoSaveUrl: String,
    hint: String,
    messageId: String,
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.fieldTarget.name,
      debounce: this.debounceValue,
      label: this.identifier,
      onSaved: value => this.dispatch('saved', { detail: { value } }),
      onError: message => this.showError(message)
    });
  }

  disconnect() {
    this.autoSave.cancel();
  }

  // ── Public actions (wired from data-action) ────────────────────────────────
  handleInput() {
    this.updateCount();
    this.clearError();
    if (this.autoSaveUrlValue) this.autoSave.save(this.fieldTarget.value);
  }

  // ── Character counter ─────────────────────────────────────────────────────
  updateCount() {
    if (!this.hasCountTarget) return;
    const max = this.fieldTarget.getAttribute('maxlength');
    this.countTarget.textContent = `${this.fieldTarget.value.length} of ${max} characters`;
  }

  // ── Error / hint region ───────────────────────────────────────────────────
  // The hint is a Ui::HintComponent; its text lives in the `[data-hint-text]`
  // span and the error icon/colour swap is CSS-driven by the root's `--error`
  // modifier.
  showError(message) {
    this.element.classList.add('ui-textarea--error');
    this.fieldTarget.setAttribute('aria-invalid', 'true');
    if (!this.hasMessageTarget) return;

    this.setMessageText(message);
    this.messageTarget.setAttribute('role', 'alert');
    this.messageTarget.hidden = false;
    this.describeByMessage(true);
  }

  clearError() {
    if (!this.element.classList.contains('ui-textarea--error')) return;

    this.element.classList.remove('ui-textarea--error');
    this.fieldTarget.setAttribute('aria-invalid', 'false');
    if (!this.hasMessageTarget) return;

    // Server-rendered errors carry the variant class and role on the hint
    // itself; drop them so the restored hint renders as a neutral hint.
    this.messageTarget.classList.remove('ui-hint--error');
    this.messageTarget.removeAttribute('role');
    this.setMessageText(this.hintValue);
    this.messageTarget.hidden = this.hintValue === '';
    this.describeByMessage(this.hintValue !== '');
  }

  setMessageText(text) {
    const textEl = this.messageTarget.querySelector('[data-hint-text]');
    if (textEl) textEl.textContent = text;
  }

  // React rebuilds `aria-describedby` from the regions it renders, so the
  // message id must come and go with the visible message.
  describeByMessage(present) {
    const ids = (this.fieldTarget.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter(id => id && id !== this.messageIdValue);

    if (present) ids.unshift(this.messageIdValue);

    if (ids.length) {
      this.fieldTarget.setAttribute('aria-describedby', ids.join(' '));
    } else {
      this.fieldTarget.removeAttribute('aria-describedby');
    }
  }
}
